import { Spinner } from '@/components/ui/spinner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/services';
import { useAuth } from '@/providers/auth-provider';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { desktop, useMediaQuery } from '@/hooks/useMediaQuery';
import { formatTime } from '@/lib/utils';

const ProjectList = () => {
  const { session } = useAuth();
  const { t } = useTranslation();
  const { data: projectsData, isLoading } = useQuery<ProjectTable[]>({
    networkMode: 'offlineFirst',
    queryKey: ['project', session?.user.id],
    queryFn: async ({ queryKey }) => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('client_id', queryKey[1])
        .order('pii_date', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
  });

  const isDesktop = useMediaQuery(desktop);

  if (isLoading) {
    return <Spinner />;
  }

  function getBadgeClass(status: string) {
    // Awaiting Inspection, Materials Ordered, Awaiting Install, Day of Install, Project Complete
    switch (status) {
      case 'Awaiting Inspection':
        return 'bg-yellow-50 text-yellow-600 border-yellow-100';
      case 'Materials Ordered':
        return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'Awaiting Install':
        return 'bg-green-50 text-green-600 border-green-100';
      case 'Day of Install':
        return 'bg-purple-50 text-purple-600 border-purple-100';
      case 'Project Complete':
        return 'bg-gray-50 text-gray-600 border-gray-100';
      default:
        return 'bg-yellow-50 text-yellow-600 border-yellow-100';
    }
  }
  if (!projectsData?.length) {
    return (
      <>
        <h1 className="text-2xl font-bold mb-6">{t('myProjects')}</h1>
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-6 min-h-[200px] text-center">
            <Folder className="h-12 w-12 text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold mb-2">{t('noProjectsYet')}</h2>
            <p className="text-muted-foreground">{t('projectsWillAppearHere')}</p>
          </CardContent>
        </Card>
      </>
    );
  }
  if (!isDesktop) {
    return <ProjectCards projects={projectsData} />;
  }
  return (
    <Card>
      <CardHeader className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 pb-7">
        <CardTitle className="text-xl md:text-2xl font-bold">{t('myprojects')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px] md:w-[150px]">{t('name')}</TableHead>
                <TableHead>{t('status')}</TableHead>
                <TableHead>PII</TableHead>
                <TableHead className="text-right">{t('action')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projectsData?.map((project: ProjectTable) => {
                return (
                  <TableRow key={project.project_id}>
                    <TableCell className="py-4">{project.name}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={'w-fit' + getBadgeClass(project.status!)}
                      >
                        {t(project.status?.toLowerCase().replace(/\s/g, '') || '')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {project.pii_date ? formatTime(project.pii_date) : '--'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="sm">
                        <Link
                          className="underline flex"
                          to={`/project/${project.project_id}`}
                        >
                          <Eye className="h-4 w-4 md:mr-2" />
                          <span className="hidden md:inline">{t('view')}</span>
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export function ProjectCards({ projects }: { projects: ProjectTable[] }) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  return (
    <>
      <h1 className="text-2xl font-bold mb-6">{t('myprojects')}</h1>
      <div className="grid gap-4">
        {projects.map((project) => {
          let status =
            project.status &&
            project.status.replace(/\s/g, '').toLowerCase().replace(' ', '');
          if (status) {
            status = t(status);
          } else {
            status = t('awaitinginstall');
          }

          return (
            <Card key={project.project_id}>
              <CardContent className="flex items-center justify-between p-6">
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">{project.name}</h2>
                  {project.pii_date && (
                    <p className="text-muted-foreground">
                      <span className="font-semibold">PII: </span>
                      {formatTime(project.pii_date)}
                    </p>
                  )}
                  <Badge
                    variant="outline"
                    className={
                      project?.status === 'Awaiting Install'
                        ? 'bg-green-500/10 text-green-500 border-green-500/20'
                        : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                    }
                  >
                    {status}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-auto"
                  onClick={() => navigate('/project/' + project.project_id)}
                >
                  <Eye className="h-5 w-5" />
                  <span className="sr-only">{t('viewProject')}</span>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}

export default ProjectList;
